import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["trigger", "menu", "search", "list", "hiddenInput", "triggerLabel"]
  static values = { url: String, selected: String, placeholder: String, type: String }

  connect() {
    this.items = []
    this.isOpen = false
    this._onClickOutside = this.closeOnClickOutside.bind(this)
    this._onKeydown = this.closeOnEscape.bind(this)
    this.fetchItems()
  }

  disconnect() {
    document.removeEventListener("click", this._onClickOutside)
    document.removeEventListener("keydown", this._onKeydown)
  }

  async fetchItems() {
    try {
      const res = await fetch(this.urlValue)
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      const json = await res.json()
      this.items = json.data || []
      this.renderList(this.items)
      if (this.selectedValue) this.applyInitialSelection()
    } catch (e) {
      console.error("[page-dropdown] Failed to load items:", e)
      this.listTarget.innerHTML = this.emptyMessage("Failed to load data")
    }
  }

  toggle(event) {
    event.stopPropagation()
    this.isOpen ? this.close() : this.open()
  }

  open() {
    this.menuTarget.classList.remove("hidden")
    this.isOpen = true
    this.searchTarget.value = ""
    this.renderList(this.items)
    requestAnimationFrame(() => this.searchTarget.focus())
    document.addEventListener("click", this._onClickOutside)
    document.addEventListener("keydown", this._onKeydown)
  }

  close() {
    this.menuTarget.classList.add("hidden")
    this.isOpen = false
    document.removeEventListener("click", this._onClickOutside)
    document.removeEventListener("keydown", this._onKeydown)
  }

  search() {
    const q = this.searchTarget.value.toLowerCase().trim()
    if (!q) {
      this.renderList(this.items)
      return
    }
    const filtered = this.items.filter(item => {
      const fields = this.searchFields(item).map(f => (f || "").toLowerCase())
      return fields.some(f => f.includes(q))
    })
    this.renderList(filtered)
  }

  select(event) {
    const row = event.currentTarget
    if (row.dataset.locked === "true") return

    const id = row.dataset.itemId
    const primary = row.dataset.itemPrimary
    const secondary = row.dataset.itemSecondary
    this.hiddenInputTarget.value = id
    this.hiddenInputTarget.dispatchEvent(new Event("change", { bubbles: true }))
    this.selectedValue = id
    this.updateTrigger(primary, secondary)
    this.close()
  }

  clear() {
    this.hiddenInputTarget.value = ""
    this.hiddenInputTarget.dispatchEvent(new Event("change", { bubbles: true }))
    this.selectedValue = ""
    this.resetTrigger()
    this.close()
  }

  // --- Rendering ---

  renderList(items) {
    if (!items.length) {
      this.listTarget.innerHTML = this.emptyMessage("No results found")
      return
    }

    const hasAllowedFlag = items.some(i => i.allowed !== undefined)
    const allowed = hasAllowedFlag ? items.filter(i => i.allowed !== false) : items
    const locked = hasAllowedFlag ? items.filter(i => i.allowed === false) : []

    const renderRow = (item, isLocked) => {
      const selected = this.itemId(item) === this.selectedValue
      const { primary, secondary, badges } = this.rowForType(item)

      const lockBadge = isLocked
        ? `<span class="inline-flex px-1.5 py-0.5 text-[10px] font-medium rounded bg-amber-50 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400">PLAN LIMIT</span>`
        : ""

      const rowClasses = isLocked
        ? "px-3 py-2 opacity-50 cursor-not-allowed"
        : `px-3 py-2 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors ${selected ? "bg-gray-50 dark:bg-gray-800/50" : ""}`

      const action = isLocked ? "" : `data-action="click->page-dropdown#select"`
      const textClass = isLocked ? "text-gray-400 dark:text-gray-600" : "text-gray-900 dark:text-white"
      const subTextClass = isLocked ? "text-gray-300 dark:text-gray-700" : "text-gray-500 dark:text-gray-400"

      return `<div class="${rowClasses}"
                   ${action}
                   data-item-id="${this.esc(this.itemId(item))}"
                   data-item-primary="${this.esc(primary)}"
                   data-item-secondary="${this.esc(secondary)}"
                   data-locked="${isLocked}">
        <div class="flex items-center justify-between gap-2">
          <span class="text-sm font-medium ${textClass} truncate">${this.esc(primary)}</span>
          <div class="flex items-center gap-1.5 flex-shrink-0">${badges}${lockBadge}</div>
        </div>
        <div class="text-xs ${subTextClass} mt-0.5 truncate">${this.esc(secondary)}</div>
      </div>`
    }

    let html = allowed.map(item => renderRow(item, false)).join("")

    if (locked.length > 0) {
      html += `<div class="px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-600 bg-gray-50 dark:bg-gray-900 border-y border-gray-100 dark:border-gray-800">Not in your plan</div>`
      html += locked.map(item => renderRow(item, true)).join("")
    }

    this.listTarget.innerHTML = html
  }

  pageRow(item) {
    const primary = item.name || "Unnamed Page"
    const secondary = item.category || ""
    const followers = item.followers || item.fans || 0
    const badges = followers > 0
      ? `<span class="inline-flex px-1.5 py-0.5 text-[10px] font-medium rounded bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400">${this.formatNumber(followers)} followers</span>`
      : ""
    return { primary, secondary, badges }
  }

  igRow(item) {
    const primary = item.username ? `@${item.username}` : (item.name || "Unknown")
    const secondary = item.name || ""
    const parts = []
    if (item.followers_count > 0) {
      parts.push(`<span class="inline-flex px-1.5 py-0.5 text-[10px] font-medium rounded bg-pink-50 dark:bg-pink-900/30 text-pink-600 dark:text-pink-400">${this.formatNumber(item.followers_count)} followers</span>`)
    }
    if (item.media_count > 0) {
      parts.push(`<span class="inline-flex px-1.5 py-0.5 text-[10px] font-medium rounded bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400">${this.formatNumber(item.media_count)} posts</span>`)
    }
    return { primary, secondary, badges: parts.join("") }
  }

  tiktokRow(item) {
    const primary = item.username ? `@${item.username}` : (item.display_name || "Unknown")
    const secondary = item.display_name || ""
    const parts = []
    if (item.follower_count > 0) {
      parts.push(`<span class="inline-flex px-1.5 py-0.5 text-[10px] font-medium rounded bg-black text-white dark:bg-gray-700 dark:text-gray-200">${this.formatNumber(item.follower_count)} followers</span>`)
    }
    if (item.video_count > 0) {
      parts.push(`<span class="inline-flex px-1.5 py-0.5 text-[10px] font-medium rounded bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400">${this.formatNumber(item.video_count)} videos</span>`)
    }
    return { primary, secondary, badges: parts.join("") }
  }

  rowForType(item) {
    if (this.typeValue === "instagram") return this.igRow(item)
    if (this.typeValue === "tiktok") return this.tiktokRow(item)
    return this.pageRow(item)
  }

  updateTrigger(primary, secondary) {
    const label = this.triggerLabelTarget
    label.innerHTML = `<span class="text-sm font-medium text-gray-900 dark:text-white truncate">${this.esc(primary)}</span>`
    if (secondary) {
      label.innerHTML += `<span class="text-xs text-gray-500 dark:text-gray-400 truncate">${this.esc(secondary)}</span>`
    }
    label.classList.add("flex", "flex-col", "items-start", "leading-tight")
  }

  resetTrigger() {
    const label = this.triggerLabelTarget
    label.textContent = this.placeholderValue || "Select..."
    label.classList.remove("flex", "flex-col", "items-start", "leading-tight")
  }

  applyInitialSelection() {
    const item = this.items.find(i => this.itemId(i) === this.selectedValue)
    if (item) {
      const { primary, secondary } = this.rowForType(item)
      this.updateTrigger(primary, secondary)
    }
  }

  // --- Helpers ---

  itemId(item) {
    if (this.typeValue === "instagram") return item.fb_instagram_account_id
    if (this.typeValue === "tiktok") return String(item.id)
    return item.fb_page_id
  }

  searchFields(item) {
    if (this.typeValue === "instagram") {
      return [item.username, item.name, item.fb_instagram_account_id]
    }
    if (this.typeValue === "tiktok") {
      return [item.username, item.display_name, String(item.id)]
    }
    return [item.name, item.category, item.fb_page_id]
  }

  formatNumber(n) {
    if (!n) return "0"
    if (n >= 1000000) return `${(n / 1000000).toFixed(1)}M`
    if (n >= 1000) return `${(n / 1000).toFixed(1)}K`
    return n.toString()
  }

  emptyMessage(message) {
    return `<div class="px-3 py-4 text-center text-xs text-gray-400 dark:text-gray-500">${this.esc(message)}</div>`
  }

  esc(str) {
    const d = document.createElement("div")
    d.textContent = str || ""
    return d.innerHTML
  }

  closeOnClickOutside(event) {
    if (!this.element.contains(event.target)) {
      this.close()
    }
  }

  closeOnEscape(event) {
    if (event.key === "Escape") {
      this.close()
    }
  }
}
